import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { isChrome } from 'react-device-detect';
import { useIdleTimer } from 'react-idle-timer';
import { BingeWatchingBanner } from 'components/BingeWatchingBanner/BingeWatchingBanner';
import { DEFAULT_SUB_SIZE, DEFAULT_SUB_STYLE } from 'components/Pages/Settings/PageAccessibility/PageAccessibilitySubtitles';
import ShakaPlayer from 'providers/player/ShakaPlayer';
import { usePlayer } from 'providers/player/PlayerContext';
import { PlayerSeriesEpisodesProvider } from 'providers/usePlayerSeriesEpisodes/PlayerSeriesEpisodesContext';
import { useNavigation } from 'hooks/useNavigation';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { PlayerControl, PlayerError } from 'types/Player';
import { SECONDS } from 'utils/TimeUnit';
import translate from 'utils/fnTranslate';
import {
    playerErrorsCode,
    playerErrorsCategory,
    INACTIVE_DEVICE,
    STREAM_LIMIT,
    STREAM_REPLACED,
    DRM_GW_TOO_MANY_DEVICES,
    DRM_GW_TOO_MANY_REGISTRATIONS,
    DRM_GW_UNKNOWN_ERROR,
    CONTENT_UNAVAILABLE,
    STREAM_ERRORS,
} from 'providers/player/playerConstants';
import { PlayerUISelector } from 'components/PlayerUI/PlayerUISelector';
import { ContentUnavailableAlert } from 'components/ContentUnavailableAlert/ContentUnavailableAlert';
import { useCast } from 'providers/cast/CastContext';
import { PictureWithFallback } from 'components/Picture/PictureWithFallback';
import fallbackImage from 'assets/images/hero_fallback.png';
import { setVideoAspectRatio } from 'utils/fnPlayerUI';
import { AspectRatio } from 'types/Common';
import { getPlayerUrlForPlayerProps } from 'utils/fnUrl';
import { MiniPlayerUI } from 'components/PlayerUI/MiniPlayerUI';
import { AlertDialog } from 'components/AlertDialog/AlertDialog';
import { DeviceManagerModal } from 'components/DeviceManagerModal/DeviceManagerModal';
import { StaticPageRoutes } from 'types/RouteTypes';
import { useApp, usePageUtils } from 'providers/useApp/AppContext';
import { guardedLocalStorage } from 'utils/fnStorage';
import { clearPortalRootOverride, MODAL_ROOT_ID, setPortalRootOverride, TOAST_ROOT_ID } from 'utils/fnPortalRoot';
import { MAIN_CONTENT_ID } from 'hooks/useRouteFocus/useRouteFocus';
import {
    CastImageOverlay,
    FullScreenPortalHost,
    PlayerErrorCode,
    PlayerErrorContainer,
    PlayerErrorText,
    PlayerErrorWrapper,
    PlayerObjectContainer,
    PlayerWrapperContainer,
} from './PlayerWrapper.css';

export const PLAYER_WRAPPER_CONTAINER = 'player-wrapper';
const HIDE_CONTROLS_AFTER = 2.5; // seconds

const FULLSCREEN_ELEMENT_PROPS = ['fullscreenElement', 'webkitFullscreenElement', 'mozFullScreenElement', 'msFullscreenElement'];
const REQUEST_FULLSCREEN_METHODS = ['requestFullscreen', 'webkitRequestFullscreen', 'mozRequestFullScreen', 'msRequestFullscreen'];
const EXIT_FULLSCREEN_METHODS = ['exitFullscreen', 'webkitExitFullscreen', 'mozCancelFullScreen', 'msExitFullscreen'];
const FULLSCREEN_CHANGE_EVENTS = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];

const invokeVendorMethod = (target: any, methods: string[]): Promise<void> => {
    const method = methods.find((name) => typeof target[name] === 'function');

    if (!method) {
        return Promise.reject(new Error('Fullscreen API not supported'));
    }

    try {
        return Promise.resolve(target[method]());
    } catch (error) {
        return Promise.reject(error);
    }
};

const getFullscreenElement = (): Element => FULLSCREEN_ELEMENT_PROPS.map((prop) => document[prop]).find(Boolean);
const requestFullscreen = (element: HTMLElement): Promise<void> => invokeVendorMethod(element, REQUEST_FULLSCREEN_METHODS);
const exitFullscreen = (): Promise<void> => invokeVendorMethod(document, EXIT_FULLSCREEN_METHODS);

type PropTypes = {
    loading: boolean;
    forceExcludedControls?: PlayerControl[];
    extraControls?: ReactNode;
    isFocused?: boolean;
};

export enum ControlGroup {
    BASE,
    EXTRA,
    AUDIO_SUBTITLE,
    EPISODES,
}

const InPlayerError: React.FC<{ error: PlayerError; activeControlGroup: ControlGroup; isMini: boolean; isEmbedded?: boolean }> = ({
    error,
    activeControlGroup,
    isMini,
    isEmbedded = false,
}) => {
    const { code, message, linkClickListener } = error;
    const errorRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        const linkElement = errorRef?.current?.getElementsByTagName('a')?.[0];

        if (linkElement) {
            linkElement.addEventListener('click', linkClickListener);
        }

        return () => {
            if (linkElement) {
                linkElement.removeEventListener('click', linkClickListener);
            }
        };
    }, [errorRef]);

    return (
        <PlayerErrorContainer isEmbedded={isEmbedded}>
            <PlayerErrorWrapper
                isEmbedded={isEmbedded}
                isMini={isMini}
                hide={activeControlGroup === ControlGroup.EXTRA}
                hideIfMobile={activeControlGroup === ControlGroup.AUDIO_SUBTITLE}
            >
                <PlayerErrorText ref={errorRef} dangerouslySetInnerHTML={{ __html: message }} aria-live="assertive" />
                {code && <PlayerErrorCode>{`${translate('ERR_CODE')}  ${code}`}</PlayerErrorCode>}
            </PlayerErrorWrapper>
        </PlayerErrorContainer>
    );
};

export const PlayerWrapper: React.FC<PropTypes> = ({ loading, forceExcludedControls, extraControls, isFocused }) => {
    const video = useRef<HTMLVideoElement | null>(null);

    const playerWrapperRef = useRef<HTMLDivElement>(null);
    const fullScreenPortalHostRef = useRef<HTMLDivElement>(null);
    const playerControlsRef = useRef<HTMLDivElement>(null);
    const hideLayoutTimeoutRef = useRef(null);
    const canHideControls = useRef(false);
    const keepLayoutVisibleRef = useRef(false);
    const inactivityTime = useRef(null);
    const checkInactivity = useRef(false);
    const bingeWatchIsShown = useRef(false);
    const autoCloseTimerRef = useRef(null);

    const [shouldHideLayout, setShouldHideLayout] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [dragPosition, setDragPosition] = useState({ top: 0, left: 0 });
    const [showAlert, setShowAlert] = useState(false);
    const [showWatchingAlert, setShowWatchingAlert] = useState(false);
    const [showStreamTakeoverAlert, setShowStreamTakeoverAlert] = useState(false);
    const [showDrmDeviceLimitAlert, setShowDrmDeviceLimitAlert] = useState(false);
    const [showDeviceManagerModal, setShowDeviceManagerModal] = useState(false);
    const [showDrmRemovalExceededAlert, setShowDrmRemovalExceededAlert] = useState(false);
    const [showDrmUnknownErrorAlert, setShowDrmUnknownErrorAlert] = useState(false);
    const [showContentUnavailableAlert, setShowContentUnavailableAlert] = useState(false);
    const [isIdle, setIsIdle] = useState(false);
    const [idleAlertCloseTime, setIdleAlertCloseTime] = useState(0);

    const { onBackPressed, navigate, location } = useNavigation();
    const { isDesktop } = useApp();
    const {
        setEngine,
        release,
        isPlaying,
        activeControlGroup,
        setPlayerError,
        playerError,
        hasMultipleTracks,
        playingMode,
        isMini,
        isEmbedded,
        miniPlayerProps,
        asset,
        engine: currentEngine,
        nextEpisode,
        displayBingeWatch,
        bingeWatchDuration,
        dismissBingeWatchingBanner,
        loadNextEpisode,
        streamEndReached,
        setPlayingState,
        setStreamEndReached,
    } = usePlayer();
    const isFullPlayer = !isMini && !isEmbedded;
    const { config } = useConfig();
    const {
        castConnected,
        tooManyDevices: castTooManyDevices,
        setTooManyDevices: setCastTooManyDevices,
        sessionLimit: castSessionLimit,
        setSessionLimit: setCastSessionLimit,
        streamTakeover: castStreamTakeover,
        setStreamTakeover: setCastStreamTakeover,
        recastPending,
        setRecastPending,
        toggleConnect,
    } = useCast();

    const { isEpg } = usePageUtils();

    const resizeTimeout = useRef<number>(null);

    const videoAspectRatio: AspectRatio = { width: 16, height: 9 };

    const inactivityTimeout = config.app_config.player_settings.binge_watching_inactivity_timeout;

    const subtitleSize = guardedLocalStorage.getItem('subSize') || DEFAULT_SUB_SIZE;
    const subtitleStyle = guardedLocalStorage.getItem('subStyle') || DEFAULT_SUB_STYLE;

    useIdleTimer({
        onIdle: () => setIsIdle(true),
        onActive: () => setIsIdle(false),
        timeout: 3 * 60 * 60 * 1000, // 3h // NOTE: binge_watching_inactivity_timeout is currently 60000
        throttle: 500,
    });

    const mouseMoveTimeoutHandler = () => {
        if (!isFocused && !keepLayoutVisibleRef.current) {
            setShouldHideLayout(canHideControls.current);
        }
    };

    const setLayoutHidden = (isHidden = true) => {
        if (hideLayoutTimeoutRef.current) {
            clearTimeout(hideLayoutTimeoutRef.current);
        }

        if (!isHidden || isFocused || keepLayoutVisibleRef.current) {
            setShouldHideLayout(false);
        } else {
            hideLayoutTimeoutRef.current = setTimeout(mouseMoveTimeoutHandler, SECONDS.toMillis(HIDE_CONTROLS_AFTER));
        }
    };

    const holdLayout = (hold: boolean) => {
        keepLayoutVisibleRef.current = hold;
        setLayoutHidden(!hold);
    };

    const hideLayoutNow = () => {
        if (hideLayoutTimeoutRef.current) {
            clearTimeout(hideLayoutTimeoutRef.current);
        }

        keepLayoutVisibleRef.current = false;
        setShouldHideLayout(canHideControls.current);
    };

    const onUserEvent = () => {
        // set inactivity time for binge watch
        if (inactivityTime.current) {
            if (checkInactivity.current && !bingeWatchIsShown.current) {
                checkInactivity.current = false;
            }

            clearTimeout(inactivityTime.current);
        }

        inactivityTime.current = window.setTimeout(() => {
            if (!bingeWatchIsShown.current) {
                checkInactivity.current = true;
            }
        }, inactivityTimeout);

        // show layout
        setLayoutHidden(false);
        // hide layout after timeout
        setLayoutHidden();
    };

    const attachPlayerListeners = useCallback(() => {
        if (playerControlsRef.current) {
            playerWrapperRef.current.addEventListener('mousemove', onUserEvent);
            playerWrapperRef.current.addEventListener('click', onUserEvent);
            playerWrapperRef.current.addEventListener('touchmove', onUserEvent);
            playerWrapperRef.current.addEventListener('touchstart', onUserEvent);
        }
    }, [isMini]);

    const detachListeners = useCallback(() => {
        if (playerControlsRef.current) {
            playerWrapperRef.current.removeEventListener('mousemove', onUserEvent);
            playerWrapperRef.current.removeEventListener('click', onUserEvent);
            playerWrapperRef.current.removeEventListener('touchmove', onUserEvent);
            playerWrapperRef.current.removeEventListener('touchstart', onUserEvent);
        }
    }, [isMini]);

    const fullScreenChangeHandler = () => {
        setIsFullScreen(!!getFullscreenElement());
    };
    // More robust solution & with better error handling for the 'undefined is not a object .fullScreen' error in older Safari browsers
    const toggleFullScreen = () => {
        const playerWrapper = playerWrapperRef.current;
        const exiting = !!getFullscreenElement();

        if (!exiting && !playerWrapper) {
            return;
        }

        (exiting ? exitFullscreen() : requestFullscreen(playerWrapper)).catch((error) => {
            console.error(exiting ? 'Error exiting fullscreen:' : 'Error entering fullscreen:', error);
            setShowAlert(true);
        });
    };

    const onBackClicked = () => {
        if (isFullScreen) {
            toggleFullScreen();
        }

        onBackPressed();
    };

    const setToNormalScreen = () => {
        if (isMini && miniPlayerProps) {
            navigate(getPlayerUrlForPlayerProps(miniPlayerProps));
        }
    };

    const handleOnStopDrag = (e, ui) => {
        setDragPosition({ top: ui.y, left: ui.x });
    };

    const setupPlayback = () => {
        setLayoutHidden();
    };
    const getErrorMessage = (code: number, category?: number): string | undefined => {
        if (code && playerErrorsCode[code]) {
            return translate(playerErrorsCode[code]);
        }

        if (category && playerErrorsCategory[category]) {
            return translate(playerErrorsCategory[category]);
        }

        return undefined;
    };

    const resizeHandler = () => {
        clearTimeout(resizeTimeout.current);
        resizeTimeout.current = window.setTimeout(() => {
            setVideoAspectRatio(video.current, videoAspectRatio, isMini ? playerWrapperRef.current : null);
        }, 200);
    };

    const closeIdleStream = () => {
        setShowWatchingAlert(false);
        setStreamEndReached(true);
        onBackPressed();
    };

    const startIdleAlertCloseTimer = () => {
        const closeTimeInSeconds = 60;
        let counter = 0;
        autoCloseTimerRef.current = setInterval(() => {
            if (counter === closeTimeInSeconds) {
                clearInterval(autoCloseTimerRef.current);
                setIdleAlertCloseTime(0);
                closeIdleStream();
                return;
            }
            counter += 1;
            setIdleAlertCloseTime((counter / closeTimeInSeconds) * 100);
        }, 1000);
    };

    useEffect(() => {
        if (isFocused) {
            setLayoutHidden(false);
        }
    }, [isFocused]);

    useEffect(() => {
        canHideControls.current = activeControlGroup === ControlGroup.BASE;
        setLayoutHidden();
    }, [isPlaying, activeControlGroup]);

    useEffect(() => {
        bingeWatchIsShown.current = displayBingeWatch;
    }, [displayBingeWatch]);

    useEffect(() => {
        let cancelled = false;

        const engine = new ShakaPlayer().setVideo(video.current);

        engine.preparePlayer({
            onInitialized: (engineInstance) => {
                if (cancelled) return;
                setEngine(engineInstance);
                setupPlayback();
                attachPlayerListeners();
            },
            errorHandler: (error) => {
                if (cancelled) return;
                const youboraInstance = currentEngine.current?.getYoubora();

                if (youboraInstance) {
                    try {
                        youboraInstance.fireFatalError(error.code, error.message, undefined);
                        youboraInstance.setOptions({ enabled: false });
                    } catch (youboraError) {
                        console.warn('NPAW - Failed to report fatal error:', youboraError);
                    }
                } else {
                    console.warn('NPAW - Cannot report fatal error - Youbora not initialized');
                }

                if (error.message === INACTIVE_DEVICE) {
                    setPlayerError(error);
                    return;
                }
                if (error.message === STREAM_LIMIT) {
                    setPlayerError(error);
                    return;
                }
                if (error.message === STREAM_REPLACED) {
                    setPlayerError(error);
                    return;
                }
                if (
                    error.message === DRM_GW_TOO_MANY_DEVICES ||
                    error.message === DRM_GW_TOO_MANY_REGISTRATIONS ||
                    error.message === DRM_GW_UNKNOWN_ERROR
                ) {
                    setPlayerError(error);
                    return;
                }

                const knownErrorMessage = getErrorMessage(error?.code, error?.category);

                if (knownErrorMessage) {
                    setPlayerError({
                        ...error,
                        ...{
                            message: knownErrorMessage,
                        },
                    });
                } else {
                    // Don't disturb the user... hopefully
                    console.error('Unknown Player Error:', error);
                }
            },
            onStreamRecovered: () => {
                if (cancelled) return;

                setPlayerError((current) =>
                    current && (STREAM_ERRORS.includes(current.message) || current.message === CONTENT_UNAVAILABLE) ? current : null
                );
            },
            onNetworkError: () => {
                // NOTE: disabling this makes more sense.. ie, the app will display
                // a full screen 'no internet' error and resume once net returns
                /*
                    setPlayerError({
                        code: NETWORK_ERROR_CODE,
                        message: getErrorMessage(NETWORK_ERROR_CODE),
                    });
                    */
            },
        });

        return () => {
            cancelled = true;
            clearTimeout(hideLayoutTimeoutRef.current);
            detachListeners();

            release().catch(() => {});
        };
    }, []);

    useEffect(() => {
        FULLSCREEN_CHANGE_EVENTS.forEach((event) => document.addEventListener(event, fullScreenChangeHandler));

        return () => {
            FULLSCREEN_CHANGE_EVENTS.forEach((event) => document.removeEventListener(event, fullScreenChangeHandler));
        };
    }, []);

    useEffect(() => {
        const host = fullScreenPortalHostRef.current;

        if (!host) {
            return undefined;
        }

        const releaseOverrides = () => {
            clearPortalRootOverride(MODAL_ROOT_ID, host);
            clearPortalRootOverride(TOAST_ROOT_ID, host);
        };

        if (isFullScreen) {
            setPortalRootOverride(MODAL_ROOT_ID, host);
            setPortalRootOverride(TOAST_ROOT_ID, host);
        } else {
            releaseOverrides();
        }

        return releaseOverrides;
    }, [isFullScreen]);

    useEffect(() => {
        const callBack = () => setVideoAspectRatio(video.current, videoAspectRatio, isMini ? playerWrapperRef.current : null);
        if (currentEngine?.current && isChrome) {
            currentEngine.current.addEventListener('loadstart', callBack);
            window.addEventListener('resize', resizeHandler);
        }

        return () => {
            if (currentEngine?.current) {
                currentEngine.current.removeEventListener('loadstart', callBack);
            }
            window.removeEventListener('resize', resizeHandler);
        };
    }, [currentEngine, isMini]);

    useEffect(() => {
        if (isChrome) {
            setVideoAspectRatio(video.current, videoAspectRatio, isMini ? playerWrapperRef.current : null);
        }

        if (playerWrapperRef.current) {
            if (!isMini) {
                playerWrapperRef.current.style.transform = 'none';
            } else {
                playerWrapperRef.current.style.transform = `translate(${dragPosition.left}px ,${dragPosition.top}px`;
            }
        }

        attachPlayerListeners();

        return () => {
            detachListeners();
        };
    }, [isMini]);

    useEffect(() => {
        if (streamEndReached && isFullScreen) {
            toggleFullScreen();
        }
    }, [streamEndReached]);

    useEffect(() => {
        if (isIdle) {
            setShowWatchingAlert(true);
            setPlayingState(false);
            startIdleAlertCloseTimer();
        } else {
            clearInterval(autoCloseTimerRef.current);
            setIdleAlertCloseTime(0);
        }
    }, [isIdle]);

    useEffect(() => {
        if (playerError && (playerError.message === STREAM_LIMIT || playerError.message === STREAM_REPLACED)) {
            setShowStreamTakeoverAlert(true);
        }
        if (playerError?.message === DRM_GW_TOO_MANY_DEVICES) {
            setShowDrmDeviceLimitAlert(true);
        }
        if (playerError?.message === DRM_GW_TOO_MANY_REGISTRATIONS) {
            setShowDrmRemovalExceededAlert(true);
        }
        if (playerError?.message === DRM_GW_UNKNOWN_ERROR) {
            setShowDrmUnknownErrorAlert(true);
        }
        if (playerError?.message === CONTENT_UNAVAILABLE) {
            setShowContentUnavailableAlert(true);
        }
    }, [playerError]);

    useEffect(() => {
        if (castTooManyDevices) {
            setShowDrmDeviceLimitAlert(true);
            setRecastPending(true);
            setCastTooManyDevices(false);
        }
    }, [castTooManyDevices]);

    useEffect(() => {
        if (castSessionLimit) {
            setShowStreamTakeoverAlert(true);
            setRecastPending(true);
            setCastSessionLimit(false);
        }
    }, [castSessionLimit]);

    useEffect(() => {
        if (castStreamTakeover) {
            setShowStreamTakeoverAlert(true);
            setRecastPending(true);
            setCastStreamTakeover(false);
        }
    }, [castStreamTakeover]);

    useEffect(() => {
        if (recastPending && !castConnected) {
            setRecastPending(false);
            toggleConnect().catch(() => {});
        }
    }, []);

    return (
        <PlayerSeriesEpisodesProvider>
            <Draggable nodeRef={playerWrapperRef} disabled={!isMini} bounds={'#body'} cancel={'.control-button'} onStop={handleOnStopDrag}>
                <PlayerWrapperContainer
                    ref={playerWrapperRef}
                    role={isFullPlayer ? 'main' : undefined}
                    id={isFullPlayer ? MAIN_CONTENT_ID : undefined}
                    tabIndex={isFullPlayer ? -1 : undefined}
                    isMini={isMini}
                    className={PLAYER_WRAPPER_CONTAINER}
                    isEpg={isEpg}
                    isMobile={!isDesktop()}
                >
                    <PlayerObjectContainer data-test-id={'player-page'}>
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- captions are added by Shaka as native tracks */}
                        <video id="player" className={`${subtitleSize} ${subtitleStyle}`} ref={video} poster="" playsInline />
                        {castConnected && (
                            <CastImageOverlay>
                                <PictureWithFallback
                                    src={asset?.image}
                                    fallbackImage={fallbackImage}
                                    fit={'cover'}
                                    hPosition={'center'}
                                    vPosition={'top'}
                                />
                            </CastImageOverlay>
                        )}
                    </PlayerObjectContainer>

                    {!isMini && (
                        <>
                            {playerError && (
                                <InPlayerError
                                    error={playerError}
                                    activeControlGroup={activeControlGroup}
                                    isMini={isMini}
                                    isEmbedded={isEmbedded}
                                />
                            )}
                            {asset && displayBingeWatch && bingeWatchDuration && nextEpisode ? (
                                <BingeWatchingBanner
                                    key={asset.id}
                                    id={asset.id}
                                    seriesId={'seriesId' in asset ? asset.seriesId : undefined}
                                    title={nextEpisode?.title}
                                    season_number={nextEpisode?.season_number}
                                    episode_number={nextEpisode?.episode_number}
                                    episode_title={nextEpisode?.episode_title}
                                    image={nextEpisode?.image_url}
                                    timeLeft={bingeWatchDuration}
                                    loadNextEpisode={loadNextEpisode}
                                    dismissBanner={dismissBingeWatchingBanner}
                                    showLimitNotification={checkInactivity.current}
                                />
                            ) : (
                                <PlayerUISelector
                                    isFullScreen={isFullScreen}
                                    shouldHideLayout={shouldHideLayout && !castConnected}
                                    setLayoutHidden={setLayoutHidden}
                                    holdLayout={holdLayout}
                                    hideLayoutNow={hideLayoutNow}
                                    onControlsRef={(ref) => {
                                        playerControlsRef.current = ref;
                                    }}
                                    onBackClicked={onBackClicked}
                                    toggleFullScreen={toggleFullScreen}
                                    forceExcludedControls={forceExcludedControls}
                                    extraControls={extraControls}
                                    loading={loading}
                                    hasTracks={hasMultipleTracks}
                                    playingMode={playingMode}
                                    casting={castConnected}
                                    isEmbedded={isEmbedded}
                                />
                            )}
                        </>
                    )}
                    {isMini && (
                        <>
                            {playerError && <InPlayerError error={playerError} activeControlGroup={activeControlGroup} isMini={isMini} />}
                            <MiniPlayerUI loading={loading} setToNormalScreen={setToNormalScreen} />
                        </>
                    )}
                    {showAlert && (
                        <AlertDialog
                            title={translate('ERR_GENERIC_TITLE')}
                            bodyText={translate('NOTIFICATION_BROWSER_NOT_SUPPORTED')}
                            buttons={[
                                {
                                    text: translate('CLOSE_BUTTON'),
                                    onClick: () => {
                                        setShowAlert(false);
                                    },
                                },
                            ]}
                        />
                    )}
                    {showWatchingAlert && (
                        <AlertDialog
                            title={translate('BINGE_WATCHING_LIMIT_TITLE')}
                            transparent
                            buttons={[
                                {
                                    text: translate('EXIT_BUTTON'),
                                    onClick: () => closeIdleStream(),
                                    progress: idleAlertCloseTime,
                                },
                                {
                                    text: translate('CONTINUE_BUTTON'),
                                    onClick: () => {
                                        setShowWatchingAlert(false);
                                        setPlayingState(true);
                                    },
                                },
                            ]}
                        />
                    )}
                    {showStreamTakeoverAlert && (
                        <AlertDialog
                            title={translate('TAKE_OVER_ERROR_SCREEN_TITLE')}
                            bodyText={translate('TAKE_OVER_ERROR_SCREEN_HINT')}
                            buttons={[
                                {
                                    text: translate('CLOSE_BUTTON'),
                                    onClick: () => {
                                        setShowStreamTakeoverAlert(false);
                                        setRecastPending(false);
                                        closeIdleStream();
                                    },
                                },
                                {
                                    text: translate('MANAGE_STREAMS_BUTTON'), // wrong translation in et imo
                                    onClick: () => {
                                        setShowStreamTakeoverAlert(false);
                                        setStreamEndReached(true);
                                        navigate(StaticPageRoutes.TAKEOVER, {
                                            state: { returnTo: location.pathname + location.search },
                                        });
                                    },
                                },
                            ]}
                        />
                    )}
                    {showDrmDeviceLimitAlert && (
                        <AlertDialog
                            title={translate('ERR_DRM_TOO_MANY_DEVICES_TITLE')}
                            bodyText={translate('ERR_DRM_TOO_MANY_DEVICES_HINT')}
                            buttons={[
                                {
                                    text: translate('CLOSE_BUTTON'),
                                    onClick: () => {
                                        setShowDrmDeviceLimitAlert(false);
                                        setRecastPending(false);
                                        closeIdleStream();
                                    },
                                },
                                {
                                    text: translate('DEVICE_MANAGEMENT_BUTTON'),
                                    onClick: () => {
                                        setShowDrmDeviceLimitAlert(false);
                                        setShowDeviceManagerModal(true);
                                    },
                                },
                            ]}
                        />
                    )}
                    {showDrmRemovalExceededAlert && (
                        <AlertDialog
                            title={translate('ERR_DRM_TOO_MANY_REGISTRATIONS_TITLE')}
                            bodyText={translate('ERR_DRM_TOO_MANY_REGISTRATIONS_HINT')}
                            buttons={[
                                {
                                    text: translate('CLOSE_BUTTON'),
                                    onClick: () => {
                                        setShowDrmRemovalExceededAlert(false);
                                        closeIdleStream();
                                    },
                                },
                            ]}
                        />
                    )}
                    {showDrmUnknownErrorAlert && (
                        <AlertDialog
                            title={translate('ERR_GENERIC_TITLE')}
                            bodyText={translate('ERR_DRM_UNKNOWN_HINT')}
                            buttons={[
                                {
                                    text: translate('CLOSE_BUTTON'),
                                    onClick: () => {
                                        setShowDrmUnknownErrorAlert(false);
                                        closeIdleStream();
                                    },
                                },
                            ]}
                        />
                    )}
                    {showContentUnavailableAlert && (
                        <ContentUnavailableAlert
                            onClose={() => {
                                setShowContentUnavailableAlert(false);
                                setPlayerError(null);
                            }}
                        />
                    )}
                    {showDeviceManagerModal && (
                        <DeviceManagerModal
                            onClose={() => {
                                setShowDeviceManagerModal(false);
                                setRecastPending(false);
                                closeIdleStream();
                            }}
                            onDeviceRemoved={() => {
                                setShowDeviceManagerModal(false);
                                if (recastPending) {
                                    setRecastPending(false);
                                    toggleConnect().catch(() => {});
                                } else {
                                    window.location.reload();
                                }
                            }}
                        />
                    )}
                    <FullScreenPortalHost ref={fullScreenPortalHostRef} />
                </PlayerWrapperContainer>
            </Draggable>
        </PlayerSeriesEpisodesProvider>
    );
};
